import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { GoldButton, GhostButton, StatBadge } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Car, Fuel, Upload, Users, Briefcase, Pencil } from "lucide-react";

const CATEGORIES = [
  { key: "economy", label: "Economy", cap_pax: "1-3" },
  { key: "executive", label: "Executive", cap_pax: "1-4" },
  { key: "xl_group", label: "XL / Grupo", cap_pax: "5-8" },
];
const FUELS = [
  { k: "diesel", l: "Gasóleo" },
  { k: "petrol", l: "Gasolina" },
  { k: "hybrid", l: "Híbrido" },
  { k: "plug_in_hybrid", l: "Híbrido plug-in" },
  { k: "electric", l: "Eléctrico" },
  { k: "lpg", l: "GPL" },
];
const FEATURES = ["wifi", "water", "child_seat", "accessibility", "ac"];
const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
}

export default function FleetVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/fleet/vehicles").then((r) => setVehicles(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 max-w-[1600px]" data-testid="fleet-vehicles-page">
      <PortalPageHeader
        eyebrow="Activos"
        title="Veículos"
        description="Adicione e gerencie os veículos da sua frota. Cada veículo precisa de aprovação do Admin DiTours antes de ser usado em viagens."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="add-fleet-vehicle">
          <Plus className="w-4 h-4" /> Adicionar veículo
        </GoldButton>
      </PortalPageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {vehicles.length === 0 && (
          <div className="col-span-3 bg-white border border-dashed border-slate-300 p-16 text-center text-slate-500">Sem veículos ainda.</div>
        )}
        {vehicles.map((v) => (
          <div key={v.id} className="bg-white border border-slate-200 overflow-hidden group" data-testid={`fleet-veh-${v.id}`}>
            <div className="relative w-full h-40 bg-slate-100 border-b border-slate-200 overflow-hidden">
              {v.main_photo ? (
                <img src={v.main_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                  <Car className="w-10 h-10" />
                </div>
              )}
              <div className="absolute top-2 right-2"><StatBadge status={v.status} /></div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold mb-1">{v.type?.replace("_", " ")} · {v.year}</div>
              <h3 className="font-display text-xl text-[#0a0f1e]">{v.brand} {v.model}</h3>
              <div className="text-sm text-slate-500 font-mono tracking-wider">{v.plate} · {v.color}</div>
              <div className="mt-3 text-xs text-slate-700 grid grid-cols-2 gap-1.5">
                <div className="flex items-center gap-1.5"><Users className="w-3 h-3 text-slate-400" /> {v.capacity_passengers} pax</div>
                <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-slate-400" /> {v.capacity_luggage} malas</div>
                <div className="flex items-center gap-1.5"><Fuel className="w-3 h-3 text-slate-400" /> {v.fuel_type || "—"}</div>
                <div className="text-slate-500">
                  {v.insurance_expiry ? <>Seg: {v.insurance_expiry}</> : "—"}
                </div>
              </div>
              {(v.features || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1">
                  {v.features.map((f) => (
                    <span key={f} className="text-[9px] uppercase font-bold text-slate-600 border border-slate-200 px-1.5 py-0.5">{f}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
                <button onClick={() => { setEditing(v); setShow(true); }} className="text-xs uppercase tracking-wide text-[#f59e0b] font-bold hover:underline inline-flex items-center gap-1" data-testid={`edit-fleet-veh-${v.id}`}>
                  <Pencil className="w-3 h-3" /> Editar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {show && (
        <VehicleModal
          vehicle={editing}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function VehicleModal({ vehicle, onClose, onSaved }) {
  const isEdit = !!vehicle;
  const [f, setF] = useState(vehicle || {
    brand: "", model: "", year: 2024, color: "Preto", plate: "",
    type: "executive", fuel_type: "diesel",
    capacity_passengers: 4, capacity_luggage: 3,
    features: [], insurance_expiry: "", inspection_expiry: "", main_photo: "",
  });
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const toggle = (feat) => setF({ ...f, features: (f.features || []).includes(feat) ? f.features.filter((x) => x !== feat) : [...(f.features || []), feat] });

  const uploadMain = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) { toast.error("Foto excede 4MB"); return; }
    try {
      const url = await uploadImage(file);
      setF({ ...f, main_photo: url });
    } catch (err) { toast.error(`Erro ao enviar foto: ${formatApiError(err)}`); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.main_photo && !isEdit) { toast.error("A foto do veículo é obrigatória"); return; }
    setLoading(true);
    try {
      if (isEdit) {
        const { id: _id, created_at: _c, ...patch } = f;  // eslint-disable-line no-unused-vars
        await api.patch(`/fleet/vehicles/${vehicle.id}`, patch);
        toast.success("Veículo actualizado");
      } else {
        await api.post("/fleet/vehicles", f);
        toast.success("Veículo criado. Admin irá aprovar em breve.");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="fleet-vehicle-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{isEdit ? `Editar veículo · ${vehicle.plate}` : "Novo veículo"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500 hover:text-[#0a0f1e]" /></button>
        </div>

        <div className="mb-6">
          <label className="label-caps block mb-2">Foto do veículo <span className="text-red-600">*</span></label>
          <div className="flex items-center gap-3">
            <div className="w-32 h-24 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {f.main_photo ? <img src={f.main_photo} alt="" className="w-full h-full object-cover" /> : <Car className="w-6 h-6 text-slate-400" />}
            </div>
            <div className="flex-1">
              <button type="button" onClick={() => fileRef.current?.click()} className="h-10 px-4 border border-slate-300 hover:border-[#f59e0b] text-xs uppercase tracking-wide font-bold inline-flex items-center gap-1" data-testid="fv-photo-upload">
                <Upload className="w-3.5 h-3.5" /> {f.main_photo ? "Substituir" : "Carregar foto"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadMain} data-testid="fv-photo-input" />
              <p className="text-[11px] text-slate-500 mt-2">JPG/PNG/WebP até 4MB.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <F l="Marca"><Input required value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className="input-dt" placeholder="Mercedes" data-testid="fv-brand" /></F>
          <F l="Modelo"><Input required value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className="input-dt" placeholder="E-Class" data-testid="fv-model" /></F>
          <F l="Ano"><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: Number(e.target.value) })} className="input-dt" data-testid="fv-year" /></F>
          <F l="Cor"><Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="input-dt" data-testid="fv-color" /></F>
          <F l="Matrícula"><Input required value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} className="input-dt" placeholder="AA-11-BB" data-testid="fv-plate" /></F>
          <F l="Categoria">
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input-dt w-full" data-testid="fv-type">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label} · {c.cap_pax} pax</option>)}
            </select>
          </F>
          <F l="Combustível">
            <select value={f.fuel_type} onChange={(e) => setF({ ...f, fuel_type: e.target.value })} className="input-dt w-full" data-testid="fv-fuel">
              {FUELS.map((fu) => <option key={fu.k} value={fu.k}>{fu.l}</option>)}
            </select>
          </F>
          <F l="Passageiros"><Input type="number" value={f.capacity_passengers} onChange={(e) => setF({ ...f, capacity_passengers: Number(e.target.value) })} className="input-dt" data-testid="fv-pax" /></F>
          <F l="Bagagem"><Input type="number" value={f.capacity_luggage} onChange={(e) => setF({ ...f, capacity_luggage: Number(e.target.value) })} className="input-dt" data-testid="fv-luggage" /></F>
          <F l="Validade seguro"><Input type="date" value={f.insurance_expiry} onChange={(e) => setF({ ...f, insurance_expiry: e.target.value })} className="input-dt" data-testid="fv-insurance" /></F>
          <F l="Validade IPO"><Input type="date" value={f.inspection_expiry} onChange={(e) => setF({ ...f, inspection_expiry: e.target.value })} className="input-dt" data-testid="fv-inspection" /></F>
          <div className="col-span-2">
            <label className="label-caps block mb-2">Extras</label>
            <div className="flex flex-wrap gap-2">
              {FEATURES.map((feat) => (
                <button key={feat} type="button" onClick={() => toggle(feat)} data-testid={`fv-feat-${feat}`}
                  className={`px-3 py-1.5 text-[10px] uppercase border font-bold ${f.features.includes(feat) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10" : "border-slate-200 text-slate-700"}`}>
                  {feat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="fv-submit">
            {loading ? "A guardar…" : (isEdit ? "Guardar alterações" : "Criar veículo")}
          </GoldButton>
        </div>
      </form>
    </div>
  );
}

function F({ l, children }) {
  return <div><label className="label-caps block mb-2">{l}</label>{children}</div>;
}
